import { addMonths, endOfMonth, isSameDay, startOfMonth } from 'date-fns'
import { State } from 'index'
import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from 'styles/components/template.module.scss'
import { Description } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { Card, CardInner } from 'components/Card'
import { StockContainer } from 'components/Stock'
import { BusinessHourLabelRow } from 'components/Card'
import { Tabs } from 'components/Tab'
import { NavButton, TextButton } from 'components/Button'
import { OpenCalendarModal } from 'components/Modal'
import { getRole, getStoreRef } from 'reducks/auth/selectors'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { useIsAfterLastOrder, useIsBeforeOrder, useIsOrderable, useNextBusinessDate } from 'hooks/store'
import { dateToString, parseTimeToDate } from 'lib/helper'
import { useEarliestBusinessTime, useLatestOrderStartTime } from 'hooks/businessHour'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { getClosedDates, getSpecialHolidays } from 'reducks/calendar/selectors'
import { BusinessTimeType } from 'types/store'
import { getHolidays, getLastOrderTime, getOrderStartTime } from 'reducks/store/selectors'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { openModal } from 'reducks/modal/operations'
import { useGetTemporaryOpen } from 'hooks/storeCalendar'
import { Role } from 'lib/role'
import { FlexRow } from 'components/Flex'
const daysOfWeek = new DaysOfWeek()

type Props = {
  page: 'released' | 'unreleased'
}

const TodayStockTemplate: FC<Props> = (props) => {
  const { page } = props

  const dispatch = useDispatch()

  const holidays = useSelector((state: State) => getHolidays(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const orderStartTime: BusinessTimeType = useSelector((state: State) => getOrderStartTime(state))
  const lastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))
  const role = useSelector((state: State) => getRole(state))

  const roleManager = new Role(role, storeRef)
  const now = useMemo(() => new Date(), [])
  const isClosed = closedDates.some((closedDate) => isSameDay(closedDate, now))
  const nextBusinessDate = useNextBusinessDate(closedDates)
  const temporaryOpen = useGetTemporaryOpen(dateToString(now), storeCalendars)
  const dayOfWeek: DayOfWeekType = daysOfWeek.getDayOfWeek(now, specialHolidays)
  const latestOrderStartTime = useLatestOrderStartTime([dayOfWeek], orderStartTime, temporaryOpen)
  const earliestLastOrderTime = useEarliestBusinessTime([dayOfWeek], lastOrderTime, 'last_order_time', temporaryOpen)
  const orderStartDateTime = parseTimeToDate(latestOrderStartTime)
  const lastOrderDateTime = parseTimeToDate(earliestLastOrderTime)
  const isOrderable = useIsOrderable(now, orderStartDateTime, lastOrderDateTime)
  const isAfterLastOrder = useIsAfterLastOrder(now, lastOrderDateTime)
  const isBeforeOrder = useIsBeforeOrder(now, orderStartDateTime, isAfterLastOrder, nextBusinessDate)

  const tabs = useMemo(
    () => [
      {
        isSelected: page === 'released',
        label: isAfterLastOrder || isClosed ? '販売予定' : '販売中',
        url: '/stock/released',
      },
      { isSelected: page === 'unreleased', label: '未販売', url: '/stock/unreleased' },
    ],
    [page, isAfterLastOrder, isClosed]
  )

  const openOpenCalendarModal = useCallback(() => {
    dispatch(
      openModal(
        <OpenCalendarModal defaultDate={now} />,
        '臨時営業日の設定',
        '臨時営業日や、通常と異なる営業時間を設定する場合は、変更したい日にちをタップしてください'
      )
    )
  }, [dispatch])

  // 直近2ヶ月の休業日を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchClosedDates(startOfMonth(now), endOfMonth(addMonths(now, 1)), holidays, specialHolidays, storeRef))
  }, [storeRef, holidays, specialHolidays])

  // 臨時休業日・営業日を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <Description
        description={'一覧から現時点の商品の在庫数と通知状況を確認できます。編集ボタンをタップすると修正が行えます。'}
      />
      <Spacer size={20} />
      <Card>
        <CardInner>
          <BusinessHourLabelRow />
        </CardInner>
      </Card>
      <Spacer size={20} />
      <FlexRow justifyContent={'end'}>
        {roleManager.hasEnoughRole(9) && (
          <TextButton label={'本日の営業時間を修正する'} onClick={openOpenCalendarModal} size={'small'} />
        )}
      </FlexRow>
      <Spacer size={20} />
      <Tabs tabs={tabs} />
      <Spacer size={73} />
      <StockContainer
        isAfterLastOrder={isAfterLastOrder}
        isBeforeOrder={isBeforeOrder}
        isClosed={isClosed}
        isOrderable={isOrderable}
        nextBusinessDate={nextBusinessDate}
        page={page}
      />
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default TodayStockTemplate
